import * as React from 'react'
import {
  ArchiveLevelDialog,
  DeleteLevelDialog,
  FutureRenewalChangeDialog,
  MembershipLevelDialog,
} from '@/pages/MembershipLevelDialogs'
import { RosterCapChangeDialog } from '@/pages/RosterCapChangeDialog'
import type {
  MembershipLevel,
  MembershipLevelDialogState,
  MembershipLevelForm,
  MembershipLevelFutureRenewalConfirmation,
  MembershipLevelRosterCapConfirmation,
} from '@/pages/MembershipLevelsPage.types'
import type { MembershipLevelMutations } from '@/pages/MembershipLevelsPage'
import type { TaxRatesResponse } from '@/pages/TaxSettingsPage'

export function MembershipLevelPageDialogs({
  dialogState,
  archiveTarget,
  deleteTarget,
  futureRenewalConfirmation,
  rosterCapConfirmation,
  mutations,
  taxRates,
  levels,
  setDialogState,
  setArchiveTarget,
  setDeleteTarget,
  onSubmit,
  onCloseFutureRenewal,
  onConfirmFutureRenewal,
  onCloseRosterCap,
  onConfirmRosterCap,
}: MembershipLevelPageDialogsProps) {
  return (
    <>
      {dialogState && (
        <MembershipLevelDialog
          key={dialogState.mode === 'edit' ? dialogState.level.id : 'new'}
          mode={dialogState.mode}
          level={dialogState.mode === 'edit' ? dialogState.level : undefined}
          pending={mutations.createLevel.isPending || mutations.updateLevel.isPending}
          error={
            dialogState.mode === 'edit' ? mutations.updateLevel.error : mutations.createLevel.error
          }
          taxRates={taxRates}
          levels={levels}
          onClose={() => setDialogState(null)}
          onSubmit={onSubmit}
        />
      )}
      {futureRenewalConfirmation && (
        <FutureRenewalChangeDialog
          confirmation={futureRenewalConfirmation}
          pending={mutations.updateLevel.isPending}
          error={mutations.updateLevel.error}
          onClose={onCloseFutureRenewal}
          onConfirm={onConfirmFutureRenewal}
        />
      )}
      {rosterCapConfirmation && (
        <RosterCapChangeDialog
          confirmation={rosterCapConfirmation}
          pending={mutations.updateLevel.isPending}
          onClose={onCloseRosterCap}
          onConfirm={onConfirmRosterCap}
        />
      )}
      {archiveTarget && (
        <ArchiveLevelDialog
          level={archiveTarget}
          pending={mutations.archiveLevel.isPending}
          onClose={() => setArchiveTarget(null)}
          onConfirm={() => mutations.archiveLevel.mutate(archiveTarget)}
        />
      )}
      {deleteTarget && (
        <DeleteLevelDialog
          level={deleteTarget}
          pending={mutations.deleteLevel.isPending}
          error={mutations.deleteLevel.error}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => mutations.deleteLevel.mutate(deleteTarget)}
        />
      )}
    </>
  )
}

interface MembershipLevelPageDialogsProps {
  dialogState: MembershipLevelDialogState
  archiveTarget: MembershipLevel | null
  deleteTarget: MembershipLevel | null
  futureRenewalConfirmation: MembershipLevelFutureRenewalConfirmation | null
  rosterCapConfirmation: MembershipLevelRosterCapConfirmation | null
  mutations: MembershipLevelMutations
  taxRates: TaxRatesResponse['tax_rates']
  levels: MembershipLevel[]
  setDialogState: React.Dispatch<React.SetStateAction<MembershipLevelDialogState>>
  setArchiveTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
  setDeleteTarget: React.Dispatch<React.SetStateAction<MembershipLevel | null>>
  onSubmit: (form: MembershipLevelForm) => void
  onCloseFutureRenewal: () => void
  onConfirmFutureRenewal: () => void
  onCloseRosterCap: () => void
  onConfirmRosterCap: () => void
}
